/**
 * FantasyPros — fantasy-sports draft / lineup assistant
 *
 * Spreads its resources across five web-accessible-resource entries, every one matched
 * to every origin with no `use_dynamic_url` — vendor SVGs under `content/svg/`, the
 * player-card and side-assistant bundles, and PNG icons under `skin/`. All are fetchable
 * from any page at a fixed URL.
 *
 * The square icon is the cleanest signal: `skin/fantasypros_icon_square_128_x_128.png`
 * is vendor-named and loads through `probeImage` with no CORS grant, surviving a
 * restrictive page CSP.
 *
 * Manifest verified against version 16.5.3.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'gfbepnlhpkbgbkcebjnfhgjckibfdfkc';

export const fantasyPros: ExtensionDefinition = {
  id: ID,
  name: 'FantasyPros: Win your Fantasy League',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'Five WAR groups on every origin with no dynamic URL. The vendor-named square icon ' +
    'is conclusive and needs no CORS grant; the logo SVG and socket script corroborate.',
  signals: [
    {
      id: 'war-square-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load skin/fantasypros_icon_square_128_x_128.png via <img> — a vendor-named icon ' +
        'exposed to every origin; needs no CORS grant, so it works under a strict CSP.',
      weight: 1,
      run: () => probeImage(ID, 'skin/fantasypros_icon_square_128_x_128.png'),
    },
    {
      id: 'war-fp-logo-svg',
      kind: 'web-accessible-resource',
      description:
        'Load content/svg/fp-logo.svg via <img> — the vendor logo, a second image path ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'content/svg/fp-logo.svg'),
    },
    {
      id: 'war-socket-event-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch content/socket-event.js — the live-update content script, an independent ' +
        'non-image path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'content/socket-event.js'),
    },
  ],
};
